import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  Chip,
  Stack
} from '@mui/material';
import {
  History as HistoryIcon,
  CheckCircle,
  Cancel,
  RadioButtonUnchecked
} from '@mui/icons-material';

// 获取状态图标
const getStatusIcon = (status) => {
  if (!status) return <RadioButtonUnchecked color="disabled" />;
  if (status === 'passed') return <CheckCircle color="success" />;
  return <Cancel color="error" />;
};

// 格式化日期
const formatDate = (dateString) => {
  const date = new Date(dateString);
  const daysAgo = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (daysAgo === 0) return '今天';
  if (daysAgo === 1) return '昨天';
  if (daysAgo < 7) return `${daysAgo}天前`;
  return date.toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit' });
};

// 解析提交内容（从 code 字段）
const parseSubmissionContent = (submission) => {
  try {
    // 尝试解析 JSON（用于选择题、简答题等）
    const parsed = JSON.parse(submission.code);
    if (parsed.type === 'multiple_choice' || (parsed.type === 'code_tracing' && parsed.answer)) {
      // 选择题：显示选择的选项
      const answer = parsed.answer;
      if (typeof answer === 'string' && answer.length === 1) {
        return `选择: ${answer.toUpperCase()}`;
      } else if (typeof answer === 'number') {
        return `选择: ${String.fromCharCode(65 + answer)}`;
      }
      return `选择: ${answer}`;
    } else if (parsed.type === 'short_answer' || parsed.type === 'proof') {
      // 简答题/证明题：显示答案内容
      const answer = parsed.answer || '';
      // 限制显示长度
      if (answer.length > 200) {
        return answer.substring(0, 200) + '...';
      }
      return answer || '无内容';
    }
    return parsed.answer || submission.code;
  } catch (e) {
    // 如果不是 JSON，可能是代码题，直接显示代码
    const code = submission.code || '';
    // 限制显示长度
    if (code.length > 300) {
      return code.substring(0, 300) + '\n... (代码过长，已截断)';
    }
    return code || '无内容';
  }
};

function SubmissionHistoryDialog({
  open,
  onClose,
  questionInfo,
  submissions
}) {
  const navigate = useNavigate();

  const handleViewQuestion = () => {
    onClose();
    if (!questionInfo) return;
    
    const questionId = questionInfo._id || questionInfo;
    const questionIdStr = questionId?.toString() || questionId;
    if (questionIdStr && questionIdStr.startsWith('notebook_')) {
      const exampleId = questionIdStr.replace('notebook_', '');
      navigate(`/notebook-exercise/${exampleId}`);
    } else {
      navigate(`/question/${questionIdStr}`);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <HistoryIcon />
          <Typography variant="h6">
            {questionInfo?.title || '未命名题目'} - 提交历史
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {submissions.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            暂无提交记录
          </Typography>
        ) : (
          <Box>
            <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
              <Stack direction="row" spacing={3}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    总提交次数
                  </Typography>
                  <Typography variant="h6">
                    {submissions.length}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    通过次数
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    {submissions.filter(s => s.status === 'passed').length}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    最高分
                  </Typography>
                  <Typography variant="h6" color="primary.main">
                    {Math.max(...submissions.map(s => s.score || 0))}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>#</strong></TableCell>
                    <TableCell><strong>提交时间</strong></TableCell>
                    <TableCell><strong>提交内容</strong></TableCell>
                    <TableCell align="center"><strong>状态</strong></TableCell>
                    <TableCell align="center"><strong>分数</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {submissions.map((sub, index) => (
                    <TableRow key={sub.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          #{submissions.length - index}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(sub.submittedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            maxWidth: 400,
                            maxHeight: 150,
                            overflow: 'auto',
                            p: 1.5,
                            bgcolor: 'background.default',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider'
                          }}
                        >
                          <Typography 
                            variant="body2" 
                            component="pre" 
                            sx={{ 
                              whiteSpace: 'pre-wrap', 
                              margin: 0,
                              fontFamily: (() => {
                                try {
                                  const parsed = JSON.parse(sub.code);
                                  // 如果是代码题，使用等宽字体；否则使用普通字体
                                  return parsed.type === 'code' || parsed.type === 'code_tracing' ? 'monospace' : 'inherit';
                                } catch {
                                  // 如果解析失败，可能是代码，使用等宽字体
                                  return 'monospace';
                                }
                              })(),
                              fontSize: '0.875rem',
                              wordBreak: 'break-word'
                            }}
                          >
                            {parseSubmissionContent(sub)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={sub.status === 'passed' ? '通过' : sub.status === 'failed' ? '未通过' : '待评分'}
                          size="small"
                          color={sub.status === 'passed' ? 'success' : sub.status === 'failed' ? 'error' : 'warning'}
                          icon={getStatusIcon(sub.status)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color={sub.status === 'passed' ? 'success.main' : 'text.primary'}
                        >
                          {sub.score}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>关闭</Button>
        {questionInfo && (
          <Button 
            variant="contained"
            onClick={handleViewQuestion}
          >
            查看题目
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default SubmissionHistoryDialog;
